/**
 * useFleetQueries.ts
 * 
 * Standalone React Query hooks for Fleet/Equipment data.
 * 
 * FleetContext.tsx internally delegates to these hooks for backward compatibility.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTallerListo } from './useTallerListo';
import { Equipment, MaintenanceLog } from '../types';
import {
    fetchEquipment, addEquipmentDoc, updateEquipmentDoc, deleteEquipmentDoc,
    fetchMaintenanceLogs, addMaintenanceLogDoc
} from '../services/database';

export const FLEET_KEYS = {
    equipment: (wsId: string) => ['equipment', wsId] as const,
    maintenanceLogs: (wsId: string) => ['maintenanceLogs', wsId] as const,
};

// La puerta de arranque (`useTallerListo`) vive en hooks/useTallerListo.ts,
// compartida con proyectos, servicios, reloj y estimados.
export function useEquipment() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: FLEET_KEYS.equipment(wsId),
        // Flecha y no `queryFn: fetchEquipment`: React Query invoca la función
        // con su propio contexto como PRIMER argumento, así que pasarla por
        // referencia le colaría el objeto de contexto donde va el id de taller.
        queryFn: () => fetchEquipment(wsId),
        enabled: listo,
    });
}

export function useMaintenanceLogs() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: FLEET_KEYS.maintenanceLogs(wsId),
        queryFn: () => fetchMaintenanceLogs(wsId),
        enabled: listo,
    });
}

export function useAddEquipment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (item: Omit<Equipment, 'id'>) => {
            await addEquipmentDoc(item);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
    });
}

export function useUpdateEquipment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<Equipment> }) => {
            await updateEquipmentDoc(id, updates);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
    });
}

export function useDeleteEquipment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await deleteEquipmentDoc(id);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
    });
}

export function useLogMaintenance() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (log: Omit<MaintenanceLog, 'id'>) => {
            await addMaintenanceLogDoc(log);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenanceLogs'] }),
    });
}
